import {
    Component,
    EventEmitter,
    forwardRef,
    inject,
    NgZone,
    OnInit,
    Output,
    ChangeDetectionStrategy
} from "@angular/core";
import _ from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusySpinnerComponent } from "~app/common/components/busy/busy-spinner/busy-spinner.component";
import { ButtonComponent } from "~app/common/components/button/button.component";
import { FileBase64, FileUploadComponent } from "~app/common/components/file-upload/file-upload.component";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { KartaFeaturelayers, RaskRoller } from "~app/common/models/constants";
import { IContent } from "~app/common/models/validationModel";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { ErrorMediatorService } from "~app/common/services/error-mediator.service";
import { FeatureService } from "~app/common/services/feature.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationMessageRowComponent } from "~app/common/validation/validation-message-row/validation-message-row.component";
import { IMapComponent } from "../../../map.models";
import { IMapGeometry, IMapSettings, IWidgetMenuButton } from "../../../map.settings";
import { MapFreehandService } from "../../../services/map-freehand.service";
import { MapQueryService } from "../../../services/map-query.service";
import { SpatialService } from "../../../services/spatial.service";
import { MapBaseSubComponent } from "../map-base-sub-component/map-base-sub.component";
import { MapImportService } from "../map-file-import/map-import.service";
import { RadiobuttonGroupComponent } from "./../../../../radiobuttongroup/radiobuttongroup.component";
import { CollectorCancelMessage, CollectorGeometryHoverMessage, CollectorListHoverMessage } from "./events/CollectorGeometryHoverMessage";

@Component({
    selector: "rask-map-collector",
    templateUrl: "./map-collector.component.html",
    styleUrls: ["./map-collector.component.scss"],
    providers: [
        {
            provide: MapBaseSubComponent,
            useExisting: forwardRef(() => MapCollectorComponent)
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BusySpinnerComponent,
        ButtonComponent,
        ValidationMessageRowComponent,
        FileUploadComponent,
        RadiobuttonGroupComponent,
        RaskCommonModule
    ]
})
export class MapCollectorComponent extends MapBaseSubComponent implements OnInit {
    readonly maxImportFileSize = 1024 * 1024 * 25;

    widgetId = "map-collector";
    buttons = [
        {
            iconClass: "fa-map-marked-alt",
            tooltipText: "Importera data",
            visible: () => this.buttonVisible(),
            disabled: this.disabled,
            widgetId: this.widgetId
        },
        {
            iconClass: "fa-times",
            tooltipText: "Avbryt",
            visible: () => this.canCancel(),
            disabled: this.disabled,
            widgetId: "map-collector-cancel",
            click: () => this.clickCancel()
        }
    ] as IWidgetMenuButton[];
    idDrawlayer = "map-collector";
    idFeaturelayer = "Collector";
    loading = false;
    map: IMapComponent;
    mapsettings: IMapSettings;
    @Output() selection = new EventEmitter<any>();
    selectionGeometries: IMapGeometry[] = [];
    @Output() visaMapFileImport = new EventEmitter<any>();
    @Output() featurelayerChanged = new EventEmitter<any>();

    protected isOnlyFPVAnvandare = true;

    protected readonly radiobuttonMapfileimportName = "mapfileimport";
    protected radioGruppValue = KartaFeaturelayers.Collector;
    protected radioImportValues: IRadiobuttonItem[] = [
        {
            key: KartaFeaturelayers.Collector,
            value: "Field Maps"
        },
        {
            key: this.radiobuttonMapfileimportName,
            value: "ForestLink / StanForD"
        }
    ];

    protected springfieldRadioImportValues: IRadiobuttonItem[] = [
        {
            key: KartaFeaturelayers.Springfield,
            value: "Springfield"
        },
        {
            key: KartaFeaturelayers.Collector,
            value: "Field Maps"
        },
        {
            key: this.radiobuttonMapfileimportName,
            value: "ForestLink / StanForD"
        }
    ];
    private fileIsLoaded = false;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly eventService = inject(EventService);
    private readonly featureService = inject(FeatureService);
    private readonly freehandService = inject(MapFreehandService);
    private readonly mapImportService = inject(MapImportService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly ngZone = inject(NgZone);
    private readonly queryService = inject(MapQueryService);
    private readonly spatialService = inject(SpatialService);
    private readonly validationService = inject(ErrorMediatorService);

    private initialLayerVisibility?: { [key: string]: boolean } = {};

    constructor() {
        super();
    }

    isReady() {
        return this.map && this.mapsettings;
    }

    buttonVisible = () => this.isReady() && !this.hideButton;

    canCancel() {
        return this.isSelecting();
    }

    clickCancel() {
        this.stop();
    }

    clickStartSelect() {
        this.map?.setClass(["interact-lasso"]);
        this.freehandService.start((selection) => this.endSelect(selection));
    }

    collectorAtgardHovered(geometri: IMapGeometry, hoverIn: boolean) {
        this.eventService.emit(new CollectorGeometryHoverMessage(geometri.attributes.GLOBALID, hoverIn));
    }

    collectorListAtgHovered(event: CollectorListHoverMessage) {
        if (event.hoverIn) {
            const drawLayer = this.mapsettings.drawLayers.find(l => l.id === this.idDrawlayer);
            const geometry = drawLayer?.geometries.find(g => g.attributes.GLOBALID === event.collectorGeometryId);
            if (geometry === undefined) {
                return;
            }
            this.map?.highlightGeometries(this.idDrawlayer, [geometry]);
        } else {
            this.map?.clearAllHighlights();
        }
    }

    hasSelection() {
        return this.isReady() && !ObjectUtils.isNullOrUndefined(this.selectionGeometries) && this.selectionGeometries.length > 0;
    }

    isSelecting() {
        return this.isReady() && this.freehandService.isDrawing;
    }

    ngOnInit(): void {
        this.featureService.getFeatureActive("VisaSpringfield")
            .pipe(takeUntil(this.ngUnsubscribe$)
            ).subscribe((visaSpringfieldEnabled: boolean) => {
                if (visaSpringfieldEnabled) {
                    this.radioImportValues = [...this.springfieldRadioImportValues];
                }
            });

        this.isOnlyFPVAnvandare = this.authenticationService.hasOnlyRoles([RaskRoller.role.Skogsvardsuppdragsplanera]);

        this.mapReady.pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(instance => {
            this.map = instance;
        });

        if (!this.isOnlyFPVAnvandare) {
            this.onShow.pipe(
                takeUntil(this.ngUnsubscribe$)
            ).subscribe(() => {
                if (ObjectUtils.isNullOrUndefined(this.map)) {
                    return;
                }
                this.loading = true;
                this.initialLayerVisibility[KartaFeaturelayers.Springfield] = this.map?.isLayerVisible(KartaFeaturelayers.Springfield);
                this.initialLayerVisibility[KartaFeaturelayers.Collector] = this.map?.isLayerVisible(KartaFeaturelayers.Collector);

                if (this.initialLayerVisibility[KartaFeaturelayers.Collector] === false && !this.isMapfileimport()) {
                    this.radioGruppValue = this.idFeaturelayer;
                    const observe = this.map?.showFeatureLayer(this.idFeaturelayer);
                    observe.pipe(
                        takeUntil(this.ngUnsubscribe$)
                    ).subscribe(x => {
                        this.loading = false;
                    });
                } else {
                    this.loading = false;
                }
            });

        } else {
            this.radioGruppValue = this.radiobuttonMapfileimportName;
        }

        this.onHide.pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(() => {
            this.stop();
            this.map?.clearClass();
        });

        this.eventService.subscribeEvent<CollectorListHoverMessage>(CollectorListHoverMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                this.collectorListAtgHovered(event);
            });

        this.eventService.subscribeEvent<CollectorCancelMessage>(CollectorCancelMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                this.clearAllSelections();
            });
    }

    preInit(mapsettings: IMapSettings) {
        this.mapsettings = mapsettings;
        let layer = this.mapsettings.featurelayers.find(fl => fl.id === this.idFeaturelayer);
        if (layer === undefined) {
            layer = {
                id: this.idFeaturelayer,
                visible: false
            };
            this.mapsettings.featurelayers.push(layer);
        }

        if (this.mapsettings.drawLayers.find(l => l.id === this.idDrawlayer) === undefined) {
            this.mapsettings.drawLayers.push({
                id: this.idDrawlayer,
                title: "Field Maps - sök",
                visible: true,
                readonly: true,
                geometries: [],
                selectable: false,
                hideInLayerList: true,
                onHoverIn: (g, m) => this.collectorAtgardHovered(g, true),
                onHoverOut: (g, m) => this.collectorAtgardHovered(g, false)
            });
        }
    }

    protected isMapfileimport(): boolean {
        return this.radioGruppValue === this.radiobuttonMapfileimportName;
    }

    protected disableRadioGrupp(): boolean {
        return this.isOnlyFPVAnvandare === true;
    }

    protected radiobuttonChanged(radioButtonValue: string) {
        this.hideCollectorAndSpringfieldLayers();
        this.stop();
        if (!this.isMapfileimport()) {
            this.idFeaturelayer = radioButtonValue;
            this.map?.showFeatureLayer(this.idFeaturelayer);
            this.featurelayerChanged.emit(this.idFeaturelayer);
        }
    }

    protected onFileChange(target: FileBase64[]) {
        const file = target[0];
        if (file) {
            if (file.size > this.maxImportFileSize) {
                const msg = {
                    message: "Filen är för stor. Max 25 MB",
                    messageType: "ValidationMessage"
                } as IContent;
                this.validationService.add(msg);
            } else {
                this.mapImportService.processFile(file);
                this.fileIsLoaded = true;
            }
        }
    }

    protected onFileSelectClick() {
        this.visaMapFileImport.emit(true);
    }

    private clearAllSelections() {
        this.map?.setGeometries(this.idDrawlayer, [], false);
        this.setGeometries([]);
        this.selectionGeometries = [];
        this.selection.emit(null);
    }

    private endSelect(selectionGeometry: IMapGeometry) {
        this.selectionGeometries = [selectionGeometry];
        this.showCollectorGeometriesInMap(selectionGeometry);
        this.showCollectorGeometriesInPopup(selectionGeometry);
        this.map?.clearClass();
    }

    private setGeometries(geometries: IMapGeometry[], color = [
        0,
        255,
        255,
        0.4
    ]) {
        geometries?.forEach(g => {
            g.visible = true;
            g.color = color;
        });
        this.map?.setGeometries(this.idDrawlayer, geometries, false);
        this.map?.showDrawLayer(this.idDrawlayer);
    }

    private showCollectorGeometriesInMap(lasso: IMapGeometry) {
        this.queryService.queryAllSublayers(this.idFeaturelayer, null, lasso).subscribe(result => {
            window.requestAnimationFrame(() => {
                const allGeometries = _.flatten(result.map(r => r.geometries));
                allGeometries.forEach(p => p.id = GuidUtils.newGuid());
                this.setGeometries(allGeometries);
            });
        });
    }

    private showCollectorGeometriesInPopup(lasso: IMapGeometry) {
        const lassoString = JSON.stringify(this.spatialService.toArcGis(lasso).geometry);
        this.ngZone.run(() => {
            this.selection.emit(lassoString);
        });
    }

    private hideCollectorAndSpringfieldLayers() {
        for (const key in this.initialLayerVisibility) {
            if (this.initialLayerVisibility[key] === false) {
                this.map?.hideFeatureLayer(key);
            }
        }
    }

    private stop() {
        if (this.fileIsLoaded) {
            this.mapImportService.closeFileImport();
            this.fileIsLoaded = false;
        } else {
            this.freehandService.stop();
            this.clearAllSelections();
            this.hideCollectorAndSpringfieldLayers();
            this.map?.clearClass();
        }
    }
}
